import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Play, Zap, Clock, DollarSign, Trophy, AlertTriangle, Loader2, Plus, X,
} from "lucide-react";
import PromptInput from "./PromptInput";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_MODELS = [
  { id: "gpt-5.2", label: "GPT-5.2", provider: "OpenAI" },
  { id: "gpt-4o", label: "GPT-4o", provider: "OpenAI" },
  { id: "gpt-4o-mini", label: "GPT-4o Mini", provider: "OpenAI" },
  { id: "claude-sonnet-4", label: "Claude Sonnet 4", provider: "Anthropic" },
  { id: "claude-haiku-4", label: "Claude Haiku 4", provider: "Anthropic" },
  { id: "gemini-3-flash", label: "Gemini 3 Flash", provider: "Google" },
  { id: "gemini-3-pro", label: "Gemini 3 Pro", provider: "Google" },
  { id: "kimi-k2.5", label: "Kimi K2.5", provider: "Moonshot AI" },
  { id: "gemma-4-27b-it", label: "Gemma 4 27B MoE", provider: "Self-Hosted" },
];

const EXAMPLE_PROMPTS = [
  "Write a haiku about artificial intelligence.",
  "Explain quantum computing to a 5-year-old in 2 sentences.",
  "Write a Python function to find the nth Fibonacci number using dynamic programming. Include type hints.",
  "Analyze the trade-offs between microservices and monolithic architectures. Consider scalability, complexity, and team size.",
];

export default function GatewayPlayground() {
  const [prompt, setPrompt] = useState("");
  const [selectedModels, setSelectedModels] = useState(["gpt-4o-mini", "gpt-4o"]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  const addModel = (modelId) => {
    if (selectedModels.length < 4 && !selectedModels.includes(modelId)) {
      setSelectedModels([...selectedModels, modelId]);
    }
  };

  const removeModel = (modelId) => {
    if (selectedModels.length > 1) {
      setSelectedModels(selectedModels.filter(m => m !== modelId));
    }
  };

  const runComparison = async () => {
    if (!prompt.trim()) {
      toast.error("Enter a prompt to compare models");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/gateway/playground`, {
        prompt: prompt.trim(),
        models: selectedModels,
      });
      setResults(res.data.results);
      setRecommendation(res.data.recommendation);
    } catch (e) {
      toast.error("Playground comparison failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-[var(--qn-primary)]" />
            Gateway Playground
          </h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">
            Test prompts against multiple models — compare costs, speed, and quality in real-time
          </p>
        </div>
      </div>

      {/* Input Area */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="p-5 space-y-4">
          {/* Example prompts */}
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_PROMPTS.map((ex, i) => (
              <button
                key={i}
                onClick={() => setPrompt(ex)}
                className="text-xs px-3 py-1.5 rounded-full bg-[var(--qn-surface)] border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40 transition-colors"
                data-testid={`example-prompt-${i}`}
              >
                {ex.slice(0, 50)}...
              </button>
            ))}
          </div>

          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={runComparison}
            placeholder="Ask anything — compare across AI models"
            disabled={loading}
            loading={loading}
            maxLength={5000}
            submitLabel="Run Comparison"
          />

          {/* Model Selection */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[var(--qn-foreground-muted)]">Compare:</span>
            {selectedModels.map(modelId => {
              const model = AVAILABLE_MODELS.find(m => m.id === modelId);
              return (
                <Badge
                  key={modelId}
                  className="bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] flex items-center gap-1 cursor-pointer"
                  data-testid={`selected-model-${modelId}`}
                >
                  {model?.label || modelId}
                  {selectedModels.length > 1 && (
                    <X className="w-3 h-3 hover:text-red-400" onClick={() => removeModel(modelId)} />
                  )}
                </Badge>
              );
            })}
            {selectedModels.length < 4 && (
              <Select onValueChange={addModel}>
                <SelectTrigger className="w-[140px] h-7 text-xs bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="add-model-select" data-automation-id="add-model-select">
                  <Plus className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Add model" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  {AVAILABLE_MODELS.filter(m => !selectedModels.includes(m.id)).map(m => (
                    <SelectItem key={m.id} value={m.id} className="text-sm">
                      {m.label} ({m.provider})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 text-sm text-[var(--qn-foreground-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Running comparison across {selectedModels.length} models...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recommendation Banner */}
      {recommendation && results && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[var(--qn-success)]/5 border border-[var(--qn-success)]/20 rounded-lg p-4 flex items-center gap-4"
          data-testid="playground-recommendation" data-automation-id="playground-recommendation"
        >
          <Trophy className="w-6 h-6 text-[var(--qn-success)]" />
          <div className="flex-1">
            <div className="font-medium text-sm">Best Value: {recommendation.best_value}</div>
            <div className="text-xs text-[var(--qn-foreground-muted)]">
              Most Cost-Effective: {recommendation.optimal || recommendation.most_cost_effective} | Fastest: {recommendation.fastest}
            </div>
          </div>
        </motion.div>
      )}

      {/* Results */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {results.map((result, i) => (
              <motion.div
                key={result.model}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card
                  className={`bg-[var(--qn-card)] border-[var(--qn-border)] h-full ${
                    recommendation?.best_value === result.model ? 'ring-1 ring-[var(--qn-success)]' : ''
                  }`}
                  data-testid={`result-card-${result.model}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        {result.model}
                        {recommendation?.best_value === result.model && (
                          <Badge className="bg-[var(--qn-success)]/10 text-[var(--qn-success)] text-xs">Best Value</Badge>
                        )}
                        {recommendation?.fastest === result.model && (
                          <Badge className="bg-blue-500/10 text-blue-400 text-xs">Fastest</Badge>
                        )}
                      </CardTitle>
                      <span className="text-xs text-[var(--qn-foreground-subtle)]">{result.provider}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1 text-[var(--qn-foreground-muted)]">
                        <Clock className="w-3 h-3" />
                        {result.latency_ms.toFixed(0)}ms
                      </span>
                      <span className="flex items-center gap-1 text-[var(--qn-foreground-muted)]">
                        <DollarSign className="w-3 h-3" />
                        ${result.estimated_cost.toFixed(6)}
                      </span>
                      <Badge variant="outline" className="text-xs border-[var(--qn-border)]">
                        {result.complexity?.level}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {result.error ? (
                      <div className="text-sm text-red-400 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{result.error}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-[var(--qn-foreground)] bg-[var(--qn-background)] rounded-lg p-3 max-h-[200px] overflow-y-auto whitespace-pre-wrap font-mono text-xs leading-relaxed">
                        {result.response}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
